import { Theme, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { CaymanPolicyFooter } from '@/components/Layout/CaymanPolicyFooter';
import { Page } from '@/components/Layout/Page/Page';
import {
  PageNavBar,
  PageTitleContent,
} from '@/components/Layout/Page/Page.styled';
import PolicyFooter from '@/components/Layout/PolicyFooter';
import { OnboardingGuide } from '@/components/OnboardingGuide';

import { useResponsive } from '@/hooks/useResponsive';
import { useTranslate } from '@/hooks/useTranslate';

import { CountryISO } from '@/types/places';

import { getDataTestId } from '@/utils/browser';
import { isCaymanDomain } from '@/utils/domain';

import { CountrySelectionForm } from './components/forms/CountrySelectionForm';

import {
  CountrySelectionDescription,
  HighlightTitle,
} from './CountrySelection.styled';

export function CountrySelectionPage() {
  const { t, language } = useTranslate();
  const [selectedCountry, setSelectedCountry] = useState('');
  const { isMobile } = useResponsive();
  const theme = useTheme();

  const onCountryChange = (value: string) => {
    setSelectedCountry(value);
  };

  return (
    <Page
      paperProps={{
        color: (theme: Theme) => theme.palette.neutral[1000],
        position: 'right',
        size: 'big',
        children: isMobile ? null : <OnboardingGuide />,
      }}
      hasLogo
    >
      <PageTitleContent {...getDataTestId('country-selection-page-title')}>
        <Trans
          i18nKey="countrySelect.title"
          lang={language}
          debug
          components={{
            a1: <HighlightTitle />,
            a2: <HighlightTitle />,
          }}
        />
      </PageTitleContent>
      <CountrySelectionForm onCountryChange={onCountryChange}>
        <CountrySelectionDescription>
          {t('countrySelect.description')}
        </CountrySelectionDescription>
      </CountrySelectionForm>
      {selectedCountry && (
        <PageNavBar paddingBottom={isMobile ? theme.spacing(4) : 0}>
          {getPolicyFooter(selectedCountry)}
        </PageNavBar>
      )}
    </Page>
  );
}

function isCaymanSelected(selectedCountry: string) {
  return isCaymanDomain || selectedCountry === CountryISO.Cayman;
}

const getPolicyFooter = (selectedCountry: string) => {
  if (isCaymanSelected(selectedCountry)) {
    return <CaymanPolicyFooter countryCode={selectedCountry} />;
  }
  return <PolicyFooter countryCode={selectedCountry} />;
};

export default CountrySelectionPage;
